import { captureException, withScope } from '@sentry/browser';

import { auth } from '@src/firebase/firebase';

import { isBenignServiceWorkerError } from './isBenignServiceWorkerError';
import { isExpectedAuthError } from './isExpectedAuthError';
import { isExpectedWorkJourneyMatrixCreateError } from './isExpectedWorkJourneyMatrixCreateError';
import { isExpectedWorkJourneyMemberIssue } from './workJourneyExpectedMemberError';

type SentryExtraInfo = Record<string, unknown>;

const isExpectedOrBenignClientError = (error: unknown): boolean =>
  isExpectedAuthError(error) ||
  isExpectedWorkJourneyMemberIssue(error) ||
  isExpectedWorkJourneyMatrixCreateError(error) ||
  isBenignServiceWorkerError(error);

/**
 * Function to send errors to Sentry
 */
const sendErrorToSentry = (error: unknown, additionalInfo: SentryExtraInfo = {}): void => {
  if (isExpectedOrBenignClientError(error)) {
    return;
  }

  let normalizedError: Error;
  if (error instanceof Error) {
    normalizedError = error;
  } else {
    normalizedError = new Error(
      typeof error === 'object' ? JSON.stringify(error) : String(error),
    );
  }

  if (isExpectedOrBenignClientError(normalizedError)) {
    return;
  }

  const extraInfo: SentryExtraInfo = {
    ...additionalInfo,
    ...(auth?.currentUser && { uid: auth.currentUser.uid }),
  };

  withScope(scope => {
    if (Object.values(extraInfo).length > 0) {
      Object.keys(extraInfo).forEach(key => {
        scope.setExtra(key, extraInfo[key]);
      });
    }
    captureException(normalizedError);
  });
};

export { sendErrorToSentry };
